import { debounce } from 'lodash';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';

/**
 * Debounce for a re-measure. Every remeasure re-renders every visible PDF page
 * at the new width, and a container that is mid-animation reports a new size
 * on each frame, so this waits for the size to settle.
 */
const REMEASURE_DELAY_MS = 300;
/**
 * Trimmed off a container narrower than `maxWidth` so the page clears its own
 * scrollbar. The observer has to apply it too: without it a container sitting
 * exactly at `maxWidth` compares equal to one just past it, and the pages stay
 * 5px narrower than they should be.
 */
const PAGE_WIDTH_INSET = 5;

export const usePdfSize = (options: {
  maxWidth: number;
  scrollableRef: MutableRefObject<HTMLDivElement>;
  pdfContainerRef: MutableRefObject<HTMLDivElement>;
}) => {
  const { maxWidth, scrollableRef, pdfContainerRef } = options;
  // Bumped whenever something happens that could change the measurement. The
  // memo below reads the DOM on the render that follows.
  const [remeasureToken, setRemeasureToken] = useState(0);

  const remeasureDebounced = useMemo(
    () =>
      debounce(
        () => setRemeasureToken((token) => token + 1),
        REMEASURE_DELAY_MS
      ),
    []
  );
  const { height, width } = useMemo(() => {
    if (pdfContainerRef.current && scrollableRef.current) {
      const { top } = scrollableRef.current.getBoundingClientRect();
      const offsetWidth = pdfContainerRef.current.offsetWidth;
      const width =
        offsetWidth > maxWidth ? maxWidth : offsetWidth - PAGE_WIDTH_INSET;

      return {
        height: scrollableRef.current.offsetHeight - top,
        width,
      };
    }

    return {};
    // remeasureToken - to recompute whenever the container or window resizes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    maxWidth,
    scrollableRef.current,
    pdfContainerRef.current,
    remeasureToken,
  ]);

  // The container resizes without the window doing so more often than not: a
  // drawer sidebar collapsing, or the SkySlope embed's card animating from the
  // 360px panel out to its document width. Measuring on `window.resize` alone
  // left those cases rendering every page at whatever width the container
  // happened to have on the first frame after mount.
  useEffect(() => {
    const container = pdfContainerRef.current;

    if (typeof ResizeObserver === 'undefined' || container == null) {
      return;
    }

    // Width changes only, and the width the pages are actually drawn at: the
    // container is `height: max-content`, so pages streaming in resize it
    // constantly, and past `maxWidth` a wider container still renders the same
    // page. Comparing the raw `offsetWidth` would re-render every page for a
    // change the pages never see.
    const measuredWidth = () =>
      container.offsetWidth > maxWidth
        ? maxWidth
        : container.offsetWidth - PAGE_WIDTH_INSET;
    let lastWidth = measuredWidth();
    const observer = new ResizeObserver(() => {
      if (measuredWidth() === lastWidth) {
        return;
      }

      lastWidth = measuredWidth();
      remeasureDebounced();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      remeasureDebounced.cancel();
    };
  }, [maxWidth, pdfContainerRef, remeasureDebounced]);

  useEffect(() => {
    const onResize = () => {
      const { width, height } = scrollableRef.current.getBoundingClientRect();

      if (width !== 0 && height !== 0) {
        remeasureDebounced();
      }
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [scrollableRef, remeasureDebounced]);

  return {
    height,
    width,
  };
};
